export class TdBundleBuilder extends HTMLElement {
  static SELECTORS = {
    bundleAddToCart: '[data-td-bundle-add]',
    bundleTier: '[data-td-tier-source]',
    progress: '[data-td-progress-indicator]',
    progressMessage: '[data-td-bundle-progress-message]',
    bundleSubtotal: '[data-td-bundle-subtotal]',
    bundleSavings: '[data-td-bundle-savings]',
    tierVisual: '[data-td-tier-visual]',
    subToggle: '[data-td-sub-toggle]',
    productCard: '[data-td-bundle-product-card]',
    swatch: '[data-td-bundle-card-swatch]',
    image: '[data-td-bundle-card-image]',
    selectedLabel: '[data-td-bundle-card-selected-label]',
    comparePrice: '[data-td-card-compare]',
    oneTimePrice: '[data-td-one-time-price]',
    subscriptionPrice: '[data-td-subscription-price]',
    submit: '[data-td-bundle-card-submit]',
    drawer: '[data-td-bundle-drawer]',
    drawerToggle: '[data-td-bundle-drawer-toggle]',
    drawerToggleLabel: '[data-td-bundle-drawer-toggle-label]',
    drawerToggleCount: '[data-td-bundle-drawer-toggle-count]'
  };

  constructor() {
    super();

    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    this.listenerController = new AbortController();

    this.BUNDLE_ADD_TO_CART = this.querySelectorAll(TdBundleBuilder.SELECTORS.bundleAddToCart);
    this.BUNDLE_TIERS = this.querySelectorAll(TdBundleBuilder.SELECTORS.bundleTier);
    this.BUNDLE_PROGRESS = this.querySelectorAll(TdBundleBuilder.SELECTORS.progress);
    this.BUNDLE_PROGRESS_MESSAGE = this.querySelectorAll(TdBundleBuilder.SELECTORS.progressMessage);
    this.SUB_SELECTOR_TOGGLE = this.querySelector(TdBundleBuilder.SELECTORS.subToggle);
    this.PRODUCT_CARDS = this.querySelectorAll(TdBundleBuilder.SELECTORS.productCard);
    this.BUNDLE_DRAWER = this.querySelector(TdBundleBuilder.SELECTORS.drawer);
    this.BUNDLE_DRAWER_TOGGLE = this.querySelector(TdBundleBuilder.SELECTORS.drawerToggle);
    this.BUNDLE_DRAWER_TOGGLE_LABEL = this.querySelector(TdBundleBuilder.SELECTORS.drawerToggleLabel);
    this.BUNDLE_DRAWER_TOGGLE_COUNT = this.querySelector(TdBundleBuilder.SELECTORS.drawerToggleCount);
    this.SUBSCRIPTION_LABELS = this.querySelectorAll('.td-bb__subscription-switch .__heading-label');

    this.PRICE_COMPARE = this.querySelectorAll('[data-product-price-compare]');
    this.PRICE_MIN = this.querySelectorAll('[data-product-min-price]');
    this.PRICE_FINAL = this.querySelectorAll('[data-product-price]');
    this.PRICE_FINAL_COMPARE = this.querySelectorAll('[data-product-price-final-compare]');
    this.BUNDLE_TOTAL = this.querySelectorAll('[data-td-bundle-total]');
    this.BUNDLE_SUBTOTAL = this.querySelectorAll(TdBundleBuilder.SELECTORS.bundleSubtotal);
    this.BUNDLE_SAVINGS = this.querySelectorAll(TdBundleBuilder.SELECTORS.bundleSavings);
    this.BUNDLE_STATUS = this.querySelector('[data-td-bundle-status]');
    this.BUNDLE_COUNT = this.querySelector('[data-td-bundle-count]');
    this.BUNDLE_TIER_VISUALS = this.querySelectorAll(TdBundleBuilder.SELECTORS.tierVisual);

    this.PRICE_ONE_TIME_ITEMS = this.querySelectorAll('[data-td-one-time-price]');
    this.PRICE_SUBSCRIPTION_ITEMS = this.querySelectorAll('[data-td-subscription-price]');

    this.bundleItems = [];
    this.freeGiftVariants = [];
    this.freeGiftTotalCount = 0;
    this.dynamicBoxIndex = 0;
    this.dynamicBoxId = null;
    this.sellingPlan = null;
    this.bundleId = null;
    this.skioProductPlatformId = this.dataset.tdSkioProductPlatformId || '8763942011138';
    this.skioLoaded = false;
    this.skioFailed = false;

    this.useSellingPlan = this.dataset.tdSubFirst === 'true';
    this.MIN_PRODUCTS = parseInt(this.dataset.tdBundleMin, 10) || 0;
    this.MAX_PRODUCTS = parseInt(this.dataset.tdBundleMax, 10);
    this.MAX_PRODUCTS = Number.isFinite(this.MAX_PRODUCTS) && this.MAX_PRODUCTS > 0 ? this.MAX_PRODUCTS : Infinity;
    this.bundleDiscount = (parseInt(this.dataset.tdBundleDiscount, 10) || 0) / 100;
    this.subBundleDiscount = (parseInt(this.dataset.tdBundleSubDiscount, 10) || 0) / 100;
    this.totalBundlePrice = 0;
    this.totalBundleBeforePrice = 0;

    this.ACTIVE_CLASS = 'td-active';
    this.HIDDEN_CLASS = 'td-bb__product-price--hidden';
    this.DESKTOP_MEDIA_QUERY = window.matchMedia('(min-width: 990px)');
    this.DRAWER_SCROLL_HIDE_CLASS = 'is-hidden-on-scroll';
    this.lastScrollY = window.scrollY || 0;
  }

  connectedCallback() {
    if (!this.PRODUCT_CARDS.length) return;
    if (this.listenerController.signal.aborted) {
      this.listenerController = new AbortController();
    }

    this.attachListeners();
    this.initializeProductCards();
    this.syncSellingPlanPrices();
    this.handleBundleStateChange();
    this.getSKIOdata();
    this.syncDrawerState();
    this.handleDrawerViewportVisibility();
  }

  disconnectedCallback() {
    this.listenerController.abort();
  }

  attachListeners() {
    const { signal } = this.listenerController;

    this.BUNDLE_ADD_TO_CART.forEach((button) => {
      button.addEventListener('click', () => {
        this.addBundleToCart(button);
      }, { signal });
    });

    this.PRODUCT_CARDS.forEach((card) => {
      card.querySelectorAll(TdBundleBuilder.SELECTORS.swatch).forEach((swatch) => {
        swatch.addEventListener('click', () => {
          this.updateCardSelection(card, swatch);
        }, { signal });
      });

      const submitButton = card.querySelector(TdBundleBuilder.SELECTORS.submit);

      submitButton?.addEventListener('click', () => {
        this.addSelectedVariant(card);
      }, { signal });
    });

    this.SUB_SELECTOR_TOGGLE?.addEventListener('change', () => {
      this.useSellingPlan = this.SUB_SELECTOR_TOGGLE.checked;
      document.dispatchEvent(new CustomEvent('td-bundler:selling-plan-change', { detail: { useSellingPlan: this.useSellingPlan } }));
      this.syncSellingPlanPrices();
      this.handleBundleStateChange();
    }, { signal });

    this.SUBSCRIPTION_LABELS.forEach((label) => {
      label.addEventListener('click', () => {
        const nextUseSellingPlan = label.dataset.tdPurchaseLabel === 'subscription';
        if (!this.SUB_SELECTOR_TOGGLE || this.useSellingPlan === nextUseSellingPlan) return;

        this.SUB_SELECTOR_TOGGLE.checked = nextUseSellingPlan;
        this.SUB_SELECTOR_TOGGLE.dispatchEvent(new Event('change', { bubbles: true }));
      }, { signal });
    });

    this.BUNDLE_DRAWER_TOGGLE?.addEventListener('click', () => {
      this.setDrawerExpanded(this.BUNDLE_DRAWER?.classList.contains('is-collapsed'));
    }, { signal });

    this.DESKTOP_MEDIA_QUERY.addEventListener('change', () => {
      this.syncDrawerState();
      this.handleProgressBar();
      this.handleDrawerViewportVisibility();
    }, { signal });

    window.addEventListener('scroll', () => {
      this.handleDrawerViewportVisibility();
    }, { signal, passive: true });

    document.addEventListener('td-bundler:summary-remove', (event) => {
      const { clientId } = event.detail || {};
      if (!clientId) return;

      this.bundleItems = this.bundleItems.filter((item) => item.clientId !== clientId);
      this.handleBundleStateChange();
    }, { signal });
  }

  syncDrawerState() {
    if (!this.BUNDLE_DRAWER) return;

    const isDesktop = this.DESKTOP_MEDIA_QUERY.matches;
    if (isDesktop) {
      this.BUNDLE_DRAWER.classList.remove('is-collapsed');
      this.BUNDLE_DRAWER.classList.remove(this.DRAWER_SCROLL_HIDE_CLASS);
      this.BUNDLE_DRAWER_TOGGLE?.setAttribute('aria-expanded', 'true');
      this.updateDrawerToggleCopy();
      return;
    }

    if (!this.BUNDLE_DRAWER.hasAttribute('data-user-toggled')) {
      this.BUNDLE_DRAWER.classList.add('is-collapsed');
      this.BUNDLE_DRAWER_TOGGLE?.setAttribute('aria-expanded', 'false');
      this.updateDrawerToggleCopy();
      return;
    }

    if (this.BUNDLE_DRAWER.classList.contains('is-collapsed')) {
      this.updateDrawerToggleCopy();
      return;
    }

    this.updateDrawerToggleCopy();
  }

  handleDrawerViewportVisibility() {
    if (!this.BUNDLE_DRAWER) return;

    const currentScrollY = window.scrollY || 0;
    const isDesktop = this.DESKTOP_MEDIA_QUERY.matches;
    const viewportHeight = window.innerHeight || 0;
    const documentHeight = document.documentElement.scrollHeight || 0;
    const distanceFromBottom = documentHeight - (currentScrollY + viewportHeight);
    const isScrollingUp = currentScrollY < this.lastScrollY;
    const isNearBottom = distanceFromBottom <= Math.max(viewportHeight * 0.35, 220);

    if (isDesktop) {
      this.BUNDLE_DRAWER.classList.remove(this.DRAWER_SCROLL_HIDE_CLASS);
      this.lastScrollY = currentScrollY;
      return;
    }

    if (isNearBottom && !isScrollingUp) {
      this.BUNDLE_DRAWER.classList.add(this.DRAWER_SCROLL_HIDE_CLASS);
    } else {
      this.BUNDLE_DRAWER.classList.remove(this.DRAWER_SCROLL_HIDE_CLASS);
    }

    this.lastScrollY = currentScrollY;
  }

  setDrawerExpanded(expanded) {
    if (!this.BUNDLE_DRAWER) return;

    this.BUNDLE_DRAWER.dataset.userToggled = 'true';
    this.BUNDLE_DRAWER.classList.toggle('is-collapsed', !expanded);
    this.BUNDLE_DRAWER_TOGGLE?.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    this.updateDrawerToggleCopy();
  }

  updateDrawerToggleCopy() {
    if (!this.BUNDLE_DRAWER_TOGGLE_LABEL || !this.BUNDLE_DRAWER) return;

    const isCollapsed = this.BUNDLE_DRAWER.classList.contains('is-collapsed');
    this.BUNDLE_DRAWER_TOGGLE_LABEL.textContent = isCollapsed ? 'View my bundle' : 'Close my bundle';
  }

  initializeProductCards() {
    this.PRODUCT_CARDS.forEach((card) => {
      const defaultSwatch = card.querySelector(`${TdBundleBuilder.SELECTORS.swatch}.td-active`) || card.querySelector(TdBundleBuilder.SELECTORS.swatch);
      if (defaultSwatch) {
        this.updateCardSelection(card, defaultSwatch);
      }
    });
  }

  async getSKIOdata() {
    if (!this.skioProductPlatformId) {
      this.skioFailed = true;
      this.updateStatus();
      return;
    }

    try {
      const response = await fetch('https://api.skio.com/storefront-http/get-dynamic-box', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          productPlatformId: this.skioProductPlatformId
        })
      });

      if (!response.ok) {
        this.skioFailed = true;
        this.updateStatus();
        return;
      }

      const data = await response.json();
      const sellingPlanString = Object.keys(data?.DynamicBox?.sellingPlanGroup || {})[0];

      if (sellingPlanString) {
        const urlSplit = sellingPlanString.split('/');
        this.sellingPlan = urlSplit[urlSplit.length - 1];
      }

      this.dynamicBoxId = data?.DynamicBox?.boxId || null;
      this.skioLoaded = !!(this.sellingPlan || this.dynamicBoxId);
      this.skioFailed = !this.skioLoaded;
      this.updateStatus();
    } catch (error) {
      this.skioFailed = true;
      console.error('Unable to load Skio bundle data.', error);
      this.updateStatus();
    }
  }

  updateCardSelection(card, swatch) {
    const swatches = card.querySelectorAll(TdBundleBuilder.SELECTORS.swatch);
    const images = card.querySelectorAll(TdBundleBuilder.SELECTORS.image);
    const selectedLabel = card.querySelector(TdBundleBuilder.SELECTORS.selectedLabel);
    const comparePrice = card.querySelector(TdBundleBuilder.SELECTORS.comparePrice);
    const oneTimePrice = card.querySelector(TdBundleBuilder.SELECTORS.oneTimePrice);
    const subscriptionPrice = card.querySelector(TdBundleBuilder.SELECTORS.subscriptionPrice);
    const selectedImageKey = swatch.dataset.tdBundleCardImageKey;

    swatches.forEach((item) => item.classList.toggle(this.ACTIVE_CLASS, item === swatch));
    images.forEach((image) => image.classList.toggle(this.ACTIVE_CLASS, image.dataset.tdBundleCardImageKey === selectedImageKey));

    if (selectedLabel) selectedLabel.textContent = swatch.dataset.tdBundleCardColor || swatch.dataset.tdBundleCardVariantTitle;
    if (comparePrice) comparePrice.textContent = Shopify.formatMoney(parseInt(swatch.dataset.tdBundleCardCompare, 10));
    if (oneTimePrice) oneTimePrice.textContent = Shopify.formatMoney(parseInt(swatch.dataset.tdBundleCardOneTimePrice, 10));
    if (subscriptionPrice) subscriptionPrice.textContent = Shopify.formatMoney(parseInt(swatch.dataset.tdBundleCardSubscriptionPrice, 10));

    this.updateCardControls(card);
  }

  getActiveSwatch(card) {
    return card.querySelector(`${TdBundleBuilder.SELECTORS.swatch}.td-active`) || card.querySelector(TdBundleBuilder.SELECTORS.swatch);
  }

  getRemainingCapacity() {
    if (!Number.isFinite(this.MAX_PRODUCTS)) return Number.POSITIVE_INFINITY;

    return Math.max(this.MAX_PRODUCTS - this.bundleItems.length, 0);
  }

  createBundleItemFromSwatch(swatch) {
    return {
      clientId: `bundle-item-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      productId: parseInt(swatch.dataset.tdBundleCardProductId, 10),
      variantId: parseInt(swatch.dataset.tdBundleCardVariantId, 10),
      title: swatch.dataset.tdBundleCardProductTitle,
      variantTitle: swatch.dataset.tdBundleCardVariantTitle,
      color: swatch.dataset.tdBundleCardColor || swatch.dataset.tdBundleCardVariantTitle,
      image: swatch.dataset.tdBundleCardImage,
      price: parseInt(swatch.dataset.tdBundleCardCompare, 10),
      oneTimePrice: parseInt(swatch.dataset.tdBundleCardOneTimePrice, 10),
      subscriptionPrice: parseInt(swatch.dataset.tdBundleCardSubscriptionPrice, 10),
      available: swatch.dataset.tdBundleCardAvailable === 'true'
    };
  }

  addSelectedVariant(card) {
    const activeSwatch = this.getActiveSwatch(card);
    const remainingCapacity = this.getRemainingCapacity();

    if (!activeSwatch || activeSwatch.dataset.tdBundleCardAvailable !== 'true' || remainingCapacity <= 0) return;

    this.bundleItems = [...this.bundleItems, this.createBundleItemFromSwatch(activeSwatch)];
    this.handleBundleStateChange();
  }

  handleBundleStateChange() {
    this.handleTotalPrice();
    this.handleFreeProducts();
    this.syncSellingPlanPrices();
    this.updatePriceElements();
    this.updateCardControlsForAll();
    this.updateStatus();
    this.emitBundleStateChanged();
  }

  handleTotalPrice() {
    this.totalBundleBeforePrice = this.bundleItems.reduce((total, item) => total + item.price, 0);
    this.totalBundlePrice = this.bundleItems.reduce((total, item) => {
      return total + (this.useSellingPlan ? item.subscriptionPrice : item.oneTimePrice);
    }, 0);
  }

  syncSellingPlanPrices() {
    this.SUBSCRIPTION_LABELS.forEach((elm, index) => {
      elm.classList.toggle('is-active', (this.useSellingPlan && index === 1) || (!this.useSellingPlan && index === 0));
    });

    this.PRICE_ONE_TIME_ITEMS.forEach((elm) => {
      elm.classList.toggle(this.HIDDEN_CLASS, this.useSellingPlan);
    });

    this.PRICE_SUBSCRIPTION_ITEMS.forEach((elm) => {
      elm.classList.toggle(this.HIDDEN_CLASS, !this.useSellingPlan);
    });
  }

  updatePriceElements() {
    const isComplete = this.bundleItems.length >= this.MIN_PRODUCTS;
    const savings = Math.max(this.totalBundleBeforePrice - this.totalBundlePrice, 0);

    this.PRICE_COMPARE.forEach((elm) => {
      elm.classList.toggle(this.ACTIVE_CLASS, !isComplete);
    });
    this.PRICE_MIN.forEach((elm) => {
      elm.classList.toggle(this.ACTIVE_CLASS, !isComplete);
    });
    this.PRICE_FINAL.forEach((elm) => {
      elm.classList.toggle(this.ACTIVE_CLASS, isComplete);
      if (isComplete) elm.textContent = Shopify.formatMoney(this.totalBundlePrice);
    });
    this.PRICE_FINAL_COMPARE.forEach((elm) => {
      elm.classList.toggle(this.ACTIVE_CLASS, isComplete);
      if (isComplete) elm.textContent = `(${Shopify.formatMoney(this.totalBundleBeforePrice)} Value)`;
    });
    this.BUNDLE_TOTAL.forEach((elm) => {
      elm.textContent = Shopify.formatMoney(this.totalBundlePrice);
    });
    this.BUNDLE_SUBTOTAL.forEach((elm) => {
      elm.textContent = Shopify.formatMoney(this.totalBundleBeforePrice);
    });
    this.BUNDLE_SAVINGS.forEach((elm) => {
      elm.textContent = `-${Shopify.formatMoney(savings)}`;
    });
  }

  updateStatus() {
    const itemCount = this.bundleItems.length;
    const isComplete = itemCount >= this.MIN_PRODUCTS;
    // Both purchase modes depend on Skio: subscriptions need the selling plan,
    // one-time bundles need the dynamic box id or the discount is never applied.
    const requiredSkioId = this.useSellingPlan ? this.sellingPlan : this.dynamicBoxId;
    const skioSettled = this.skioLoaded || this.skioFailed;
    const skioLoading = !requiredSkioId && !skioSettled;
    const skioUnavailable = !requiredSkioId && skioSettled;
    const hasOutOfStockItems = this.bundleItems.some((item) => !item.available);

    this.BUNDLE_ADD_TO_CART.forEach((button) => {
      button.disabled = !isComplete || skioLoading || skioUnavailable || hasOutOfStockItems;

      if (!isComplete) {
        const remaining = this.MIN_PRODUCTS - itemCount;
        button.textContent = `Add ${remaining} more item${remaining === 1 ? '' : 's'}`;
        return;
      }

      button.textContent = 'Add Bundle To Cart';
    });

    const hasMaxLimit = Number.isFinite(this.MAX_PRODUCTS);

    if (this.BUNDLE_COUNT) {
      this.BUNDLE_COUNT.textContent = hasMaxLimit ? `${itemCount} of ${this.MAX_PRODUCTS} items` : `${itemCount} item${itemCount === 1 ? '' : 's'}`;
    }

    if (this.BUNDLE_DRAWER_TOGGLE_COUNT) {
      this.BUNDLE_DRAWER_TOGGLE_COUNT.textContent = hasMaxLimit ? `${itemCount}/${this.MAX_PRODUCTS}` : `${itemCount}`;
    }

    if (!this.BUNDLE_STATUS) return;

    if (skioUnavailable) {
      this.BUNDLE_STATUS.textContent = this.useSellingPlan
        ? 'Subscriptions are currently unavailable. Please refresh and try again.'
        : 'Bundle pricing is currently unavailable. Please refresh and try again.';
    } else if (hasOutOfStockItems) {
      this.BUNDLE_STATUS.textContent = 'An item in your bundle is out of stock. Remove it to continue.';
    } else if (!isComplete) {
      const remaining = this.MIN_PRODUCTS - itemCount;
      this.BUNDLE_STATUS.textContent = `Select ${remaining} more item${remaining === 1 ? '' : 's'} to continue.`;
    } else if (skioLoading) {
      this.BUNDLE_STATUS.textContent = 'Preparing your bundle…';
    } else {
      this.BUNDLE_STATUS.textContent = 'Your bundle is ready to add to cart.';
    }
  }

  updateCardControlsForAll() {
    this.PRODUCT_CARDS.forEach((card) => this.updateCardControls(card));
  }

  updateCardControls(card) {
    const activeSwatch = this.getActiveSwatch(card);
    const submitButton = card.querySelector(TdBundleBuilder.SELECTORS.submit);
    const remainingCapacity = this.getRemainingCapacity();
    const isAvailable = activeSwatch?.dataset.tdBundleCardAvailable === 'true';

    if (!activeSwatch) return;

    if (submitButton) {
      submitButton.disabled = !isAvailable || remainingCapacity <= 0;
      submitButton.textContent = 'Add to Bundle';

      if (!isAvailable) {
        submitButton.textContent = 'Out of Stock';
      } else if (remainingCapacity <= 0) {
        submitButton.textContent = 'Bundle Full';
      }
    }
  }

  emitBundleStateChanged() {
    document.dispatchEvent(new CustomEvent('td-bundler:items-changed', {
      detail: {
        items: this.bundleItems,
        useSellingPlan: this.useSellingPlan,
        totalBundlePrice: this.totalBundlePrice,
        totalBundleBeforePrice: this.totalBundleBeforePrice,
        minProducts: this.MIN_PRODUCTS,
        maxProducts: this.MAX_PRODUCTS
      }
    }));
  }

  handleFreeProducts() {
    this.freeGiftVariants = [];
    this.freeGiftTotalCount = 0;

    this.BUNDLE_TIERS.forEach((tier) => {
      if (this.totalBundlePrice >= parseInt(tier.dataset.tdTierAmount, 10) * 100) {
        this.freeGiftTotalCount += 1;

        if (tier.dataset.tdTierType === 'free_product' && tier.dataset.tdFreeProduct) {
          this.freeGiftVariants.push(JSON.parse(tier.dataset.tdFreeProduct));
        }
      }
    });

    this.handleProgressBar();
  }

  handleProgressBar() {
    if (!this.BUNDLE_PROGRESS.length || !this.BUNDLE_TIERS.length) return;

    let progressTotalPercent = 100;

    if (this.freeGiftTotalCount < this.BUNDLE_TIERS.length) {
      const nextTierPrice = parseInt(this.BUNDLE_TIERS[this.freeGiftTotalCount]?.dataset.tdTierAmount || 0, 10) * 100;
      const percentPerTier = 100 / this.BUNDLE_TIERS.length;
      const currentPercentToNextTier = nextTierPrice > 0 ? (this.totalBundlePrice / nextTierPrice) * 100 : 0;
      progressTotalPercent = (percentPerTier * this.freeGiftTotalCount) + ((currentPercentToNextTier / 100) * percentPerTier);
    }

    const clampedPercent = Math.min(progressTotalPercent, 100);

    this.BUNDLE_PROGRESS.forEach((progressBar) => {
      const direction = progressBar.dataset.progressDirection || 'horizontal';
      if (direction === 'vertical') {
        progressBar.style.height = `${clampedPercent}%`;
        progressBar.style.width = '10px';
      } else {
        progressBar.style.width = `${clampedPercent}%`;
        progressBar.style.height = '10px';
      }
    });

    this.BUNDLE_TIER_VISUALS.forEach((elm) => {
      const index = parseInt(elm.dataset.tdTierVisualIndex || '-1', 10);
      const innerElement = elm.querySelector('.__value');
      if (!innerElement || index < 0) return;

      if ((this.freeGiftTotalCount - 1) >= index) {
        innerElement.style = 'background-color: #353535;color: #FFFFFF;';
      } else {
        innerElement.style = 'background-color: #FFFFFF;color: #353535;';
      }
    });

    this.updateProgressMessage();
  }

  updateProgressMessage() {
    if (!this.BUNDLE_PROGRESS_MESSAGE.length || !this.BUNDLE_TIERS.length) return;

    let message = 'Build your bundle to unlock rewards.';

    if (this.freeGiftTotalCount < this.BUNDLE_TIERS.length) {
      const nextTier = this.BUNDLE_TIERS[this.freeGiftTotalCount];
      const nextTierPrice = parseInt(nextTier?.dataset.tdTierAmount || 0, 10) * 100;
      const amountAway = Math.max(nextTierPrice - this.totalBundlePrice, 0);
      const nextTierLabel = nextTier?.querySelector('.__text')?.textContent?.trim() || 'your next reward';
      message = `You're ${Shopify.formatMoney(amountAway)} away from ${nextTierLabel.toLowerCase()}.`;
    } else {
      message = 'All bundle rewards unlocked.';
    }

    this.BUNDLE_PROGRESS_MESSAGE.forEach((elm) => {
      elm.textContent = message;
    });
  }

  async addBundleToCart(button) {
    if (this.bundleItems.length < this.MIN_PRODUCTS) return;
    if (this.useSellingPlan && !this.sellingPlan) return;
    if (!this.useSellingPlan && !this.dynamicBoxId) return;
    if (this.bundleItems.some((item) => !item.available)) return;

    button.disabled = true;

    const randomString = Math.random().toString(36).substring(2, 10);
    const timestamp = new Date().getTime();
    this.bundleId = `td-bundle--${randomString}-${timestamp}`;

    try {
      const groupedVariants = this.bundleItems.reduce((acc, item) => {
        if (!acc[item.variantId]) {
          acc[item.variantId] = [];
        }

        acc[item.variantId].push(item);
        return acc;
      }, {});

      const items = [];

      // Free gifts are owned by an external app keyed on `_freeGift` — they must
      // stay outside the bundle group, so no `_bundle_id` or bundle metadata here.
      this.freeGiftVariants.forEach((variant) => {
        if (!variant) return;

        items.push({
          id: variant.id,
          quantity: 1,
          properties: {
            _freeGift: true
          }
        });
      });

      Object.keys(groupedVariants).forEach((key) => {
        const item = {
          id: parseInt(key, 10),
          quantity: groupedVariants[key].length,
          properties: {
            _bundle: true,
            _bundle_id: this.bundleId,
            _bundle_title: this.dataset.tdBundleTitle,
            _bundle_image: this.dataset.tdBundleImage,
            _bundle_url: this.dataset.tdBundleUrl,
            _bundle_before_price: this.totalBundleBeforePrice,
            _bundle_price: this.totalBundlePrice
          }
        };

        if (this.useSellingPlan && this.sellingPlan) {
          item.selling_plan = this.sellingPlan;
        } else {
          item.properties._dynamicBoxIndex = this.dynamicBoxIndex;
          item.properties._dynamicBoxId = this.dynamicBoxId;
        }

        items.push(item);
      });

      const cartAttributes = await this.getCartAttributes();
      cartAttributes[`_bundle_id_${this.bundleId}`] = this.bundleId;

      const body = {
        items,
        attributes: cartAttributes
      };

      if (this.cart) {
        body.sections = this.cart.getSectionsToRender().map((section) => section.id);
      }

      const response = await fetch('/cart/add.js', {
        body: JSON.stringify(body),
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        method: 'POST'
      });

      const data = await response.json();

      if (data.status) {
        throw new Error(data.description || 'Unable to add bundle to cart.');
      }

      if (this.cart) {
        this.cart.renderContents(data);
        this.cart.classList.remove('is-empty');
      } else {
        window.location = window.routes.cart_url;
      }
    } catch (error) {
      console.error(error);
      if (this.BUNDLE_STATUS) {
        this.BUNDLE_STATUS.textContent = 'Unable to add your bundle to the cart. Please try again.';
      }
    } finally {
      button.disabled = false;
    }
  }

  getCartAttributes() {
    return fetch('/cart.js')
      .then((response) => response.json())
      .then((cart) => cart.attributes || {});
  }
}

if (!customElements.get('td-bundle-builder')) {
  customElements.define('td-bundle-builder', TdBundleBuilder);
}

export class TdBundleSummary extends HTMLElement {
  constructor() {
    super();
    this.bundleItems = [];
    this.useSellingPlan = this.dataset.isSellingPlan === 'true';
    this.minProducts = parseInt(this.dataset.tdBundleMin, 10) || 0;
    this.maxProducts = parseInt(this.dataset.tdBundleMax, 10) || this.minProducts;
    this.itemTemplate = null;
    this.placeholderTemplate = null;
    this.listenersAttached = false;
  }

  connectedCallback() {
    // Cache the Liquid-rendered templates before render() replaces our children.
    this.itemTemplate = this.itemTemplate || this.querySelector('[data-td-summary-item-template]');
    this.placeholderTemplate = this.placeholderTemplate || this.querySelector('[data-td-summary-placeholder-template]');

    if (!this.listenersAttached) {
      this.listenersAttached = true;
      this.addEventListener('click', (event) => {
        const removeButton = event.target.closest('[data-td-summary-remove]');
        if (removeButton?.dataset.clientId) this.removeItem(removeButton.dataset.clientId);
      });
    }

    document.addEventListener('td-bundler:items-changed', this.handleItemsChanged);
    document.addEventListener('td-bundler:selling-plan-change', this.handleSellingPlanChanged);
    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener('td-bundler:items-changed', this.handleItemsChanged);
    document.removeEventListener('td-bundler:selling-plan-change', this.handleSellingPlanChanged);
  }

  handleItemsChanged = (event) => {
    this.bundleItems = event.detail?.items || [];
    this.render();
  };

  handleSellingPlanChanged = (event) => {
    this.useSellingPlan = !!event.detail?.useSellingPlan;
    this.render();
  };

  removeItem(clientId) {
    document.dispatchEvent(new CustomEvent('td-bundler:summary-remove', { detail: { clientId } }));
  }

  createSummaryItem(item) {
    const node = this.itemTemplate.content.firstElementChild.cloneNode(true);

    const image = node.querySelector('[data-td-summary-field="image"]');
    if (image) {
      image.src = item.image;
      image.alt = item.title;
    }

    const title = node.querySelector('[data-td-summary-field="title"]');
    if (title) title.textContent = item.title;

    const color = node.querySelector('[data-td-summary-field="color"]');
    if (color) color.textContent = item.color;

    const price = node.querySelector('[data-td-summary-field="price"]');
    if (price) price.textContent = Shopify.formatMoney(this.useSellingPlan ? item.subscriptionPrice : item.oneTimePrice);

    const removeButton = node.querySelector('[data-td-summary-remove]');
    if (removeButton) {
      removeButton.dataset.clientId = item.clientId;
      removeButton.setAttribute('aria-label', `Remove ${item.title}`);
    }

    return node;
  }

  createPlaceholderItem() {
    return this.placeholderTemplate.content.firstElementChild.cloneNode(true);
  }

  render() {
    if (!this.itemTemplate || !this.placeholderTemplate) return;

    const visibleSlotCount = Math.max(this.bundleItems.length, this.maxProducts || 5);
    const slots = Array.from({ length: visibleSlotCount }, (_, index) => this.bundleItems[index] || null);
    const list = document.createElement('div');
    list.className = 'td-bb__summary-list';

    slots.forEach((item) => {
      list.append(item ? this.createSummaryItem(item) : this.createPlaceholderItem());
    });

    this.replaceChildren(list);
  }
}

if (!customElements.get('td-bundle-summary')) {
  customElements.define('td-bundle-summary', TdBundleSummary);
}
